import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { profileApi, ProfileResponse, ApiError } from '../../services/api';
import { LanguageSwitcher } from '../layout/LanguageSwitcher';
import layoutStyles from '../settings/MicrophoneSettingsLayout.module.css';
import styles from './ProfileSettings.module.css';

interface ProfileSettingsProps {
  onClose: () => void;
}

export function ProfileSettings({ onClose }: ProfileSettingsProps) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<ProfileResponse['data'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await profileApi.getProfile();
      setProfile(response.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('profile:failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  return (
    <div className={layoutStyles.container}>
      <div className={layoutStyles.header}>
        <h2 className={layoutStyles.title}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
          {t('profile:title')}
        </h2>
        <button
          type="button"
          className={layoutStyles.closeButton}
          onClick={onClose}
          title={t('common:buttons.close')}
          aria-label={t('common:buttons.close')}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      <div className={layoutStyles.content}>
        {isLoading ? (
          <div className={styles.profileLoading}>{t('profile:loadingProfile')}</div>
        ) : error ? (
          <div className={styles.profileError}>{error}</div>
        ) : (
          <>
            <div className={styles.profileInfo}>
              <div className={styles.profileField}>
                <span className={styles.profileLabel}>{t('profile:fields.name')}</span>
                <span className={styles.profileValue}>
                  {profile?.firstName} {profile?.lastName}
                </span>
              </div>
              <div className={styles.profileField}>
                <span className={styles.profileLabel}>{t('profile:fields.username')}</span>
                <span className={styles.profileValue}>{profile?.username}</span>
              </div>
              <div className={styles.profileField}>
                <span className={styles.profileLabel}>{t('profile:fields.cxoneAgentId')}</span>
                <span className={styles.profileValue}>{profile?.cxoneAgentId}</span>
              </div>
              <div className={styles.profileField}>
                <span className={styles.profileLabel}>{t('profile:fields.status')}</span>
                <span className={styles.profileValue}>
                  <span className={`status-badge ${profile?.status?.toLowerCase()}`}>
                    {profile?.status}
                  </span>
                </span>
              </div>
            </div>

            <div className={styles.profileSection}>
              <h3>{t('profile:language.title')}</h3>
              <div className={`${styles.profileField} ${styles.profileFieldLanguage}`}>
                <span className={styles.profileLabel}>{t('profile:language.label')}</span>
                <LanguageSwitcher />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
